import type { MovePlan, TreeDoc } from "../../lib/api/models";

export type { MovePlan };
export type TreeNode = TreeDoc & { children: TreeNode[]; depth: number };
export type DropPlace = "before" | "after" | "inside";

/** The order of siblings: by position, and by title where two share one. */
export const byPosition = (a: TreeDoc, b: TreeDoc) => a.position - b.position || a.title.localeCompare(b.title);

export function buildTree(docs: TreeDoc[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>(docs.map((d) => [d.id, { ...d, children: [], depth: 0 }]));
  const roots: TreeNode[] = [];
  for (const d of [...docs].sort(byPosition)) {
    const node = nodes.get(d.id);
    if (!node) continue;
    const parent = d.parentId ? nodes.get(d.parentId) : undefined;
    (parent ? parent.children : roots).push(node);
  }
  const setDepth = (list: TreeNode[], depth: number) => {
    for (const n of list) {
      n.depth = depth;
      setDepth(n.children, depth + 1);
    }
  };
  setDepth(roots, 0);
  return roots;
}

export function visibleRows(tree: TreeNode[], expanded: ReadonlySet<string>): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (list: TreeNode[]) => {
    for (const n of list) {
      out.push(n);
      if (expanded.has(n.id)) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

export function ancestorsOf(docs: TreeDoc[], id: string): string[] {
  const byId = new Map(docs.map((d) => [d.id, d]));
  const out: string[] = [];
  let cur = byId.get(id)?.parentId ?? null;
  while (cur && !out.includes(cur)) {
    out.unshift(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return out;
}

export function planMove(docs: TreeDoc[], dragId: string, targetId: string, place: DropPlace): MovePlan | null {
  if (dragId === targetId) return null;
  const byId = new Map(docs.map((d) => [d.id, d]));
  const drag = byId.get(dragId);
  const target = byId.get(targetId);
  if (!drag || !target) return null;
  if (ancestorsOf(docs, targetId).includes(dragId)) return null; // into its own subtree
  const parentId = place === "inside" ? target.id : target.parentId;
  const siblings = docs.filter((d) => d.parentId === parentId && d.id !== dragId).sort(byPosition);
  const index =
    place === "inside" ? siblings.length : siblings.findIndex((d) => d.id === targetId) + (place === "after" ? 1 : 0);
  const current = docs
    .filter((d) => d.parentId === drag.parentId)
    .sort(byPosition)
    .findIndex((d) => d.id === dragId);
  if (parentId === drag.parentId && index === current) return null;
  const at = siblings[index];
  const last = siblings[siblings.length - 1];
  return { parentId, position: at ? at.position : last ? last.position + 1 : 0 };
}

/**
 * For the Templates tree, whose top level is shown in groups by audience (who a template is written for). A row
 * may only be put where it will then be shown: at the top level next to rows of its own audience, or under a row
 * whose group is its own. Changing the order under one parent is always possible.
 */
export function staysInGroup(docs: TreeDoc[], dragId: string, targetId: string, place: DropPlace): boolean {
  const byId = new Map(docs.map((d) => [d.id, d]));
  const drag = byId.get(dragId);
  const target = byId.get(targetId);
  if (!drag || !target) return false;
  const parentId = place === "inside" ? target.id : target.parentId;
  if (parentId !== null && parentId === drag.parentId) return true;
  const top = byId.get(ancestorsOf(docs, targetId)[0] ?? targetId);
  return drag.audience === (parentId === null ? target.audience : top?.audience);
}

export function placeFromOffset(offsetY: number, height: number): DropPlace {
  if (offsetY < height * 0.25) return "before";
  if (offsetY > height * 0.75) return "after";
  return "inside";
}
